import { useEffect, useState } from 'react';
import { Icon, type IconName } from './ui/Icon.js';

interface Step { t: string; d: string; icon: IconName; }

const STEPS: Step[] = [
  { t: 'Reading your taste profile…',  d: 'Family size · dietary needs · cuisines',  icon: 'user'    },
  { t: 'Sourcing this week\'s recipes…',  d: 'Flavor variety · cuisine mix · seasonal picks',  icon: 'sparkle' },
  { t: 'Honoring restrictions…',       d: 'Allergens and avoid-list ingredients',    icon: 'shield'  },
  { t: 'Balancing your week…',         d: 'Variety · prep time · cuisines',          icon: 'sparkle' },
  { t: 'Pricing your shopping list…',  d: 'Targeting your weekly budget',            icon: 'wallet'  },
  { t: 'Plating it up…',               d: '21 meals, ready to review',               icon: 'plate'   },
];

export interface GeneratePlanFlowProps {
  state: 'running' | 'success' | 'error';
  errorMessage?: string | null;
  mealCount?: number;
  onReview: () => void;
  onReroll: () => void;
  onClose: () => void;
  // Phase E — family intelligence constraint badges
  blwActive?: boolean;
  hiddenVeggieMode?: boolean;
  pickyEaterMode?: boolean;
}

export function GeneratePlanFlow({
  state,
  errorMessage,
  mealCount = 21,
  onReview,
  onReroll,
  onClose,
  blwActive = false,
  hiddenVeggieMode = false,
  pickyEaterMode = false,
}: GeneratePlanFlowProps) {
  const [step, setStep] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (state === 'running') {
      setStep(0);
      setShowSuccess(false);
    }
  }, [state]);

  useEffect(() => {
    if (state === 'error') return;
    if (step >= STEPS.length) return;
    const delay = state === 'success' ? 220 : (step === 0 ? 700 : 900);
    const t = setTimeout(() => setStep((s) => s + 1), delay);
    return () => clearTimeout(t);
  }, [step, state]);

  useEffect(() => {
    if (state === 'success' && step >= STEPS.length) {
      const t = setTimeout(() => setShowSuccess(true), 600);
      return () => clearTimeout(t);
    }
  }, [state, step]);

  const isError = state === 'error';
  const fillPct = Math.min(100, (step / STEPS.length) * 100);
  const glow = isError
    ? 'color-mix(in oklab, var(--danger) 18%, transparent)'
    : 'var(--accent-glow)';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Generating your meal plan"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'var(--bg-deep)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
        overflow: 'auto',
        animation: 'pp-fadeIn .3s ease',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `radial-gradient(circle at 50% 50%, ${glow} 0%, transparent 50%)`,
          opacity: showSuccess ? 0.95 : 0.55,
          transition: 'opacity 1s ease',
        }}
      />

      <button
        onClick={onClose}
        aria-label="Close"
        className="btn btn-ghost"
        style={{
          position: 'absolute',
          top: 18,
          right: 18,
          padding: 10,
          borderRadius: '50%',
          width: 40,
          height: 40,
        }}
      >
        <Icon name="x" size={16} />
      </button>

      <div style={{ width: '100%', maxWidth: 720, position: 'relative' }}>
        {isError ? (
          <ErrorPanel
            message={errorMessage ?? 'Generation failed.'}
            onReroll={onReroll}
            onClose={onClose}
          />
        ) : !showSuccess ? (
          <ProgressPanel
            step={step}
            fillPct={fillPct}
            blwActive={blwActive}
            hiddenVeggieMode={hiddenVeggieMode}
            pickyEaterMode={pickyEaterMode}
          />
        ) : (
          <SuccessPanel
            mealCount={mealCount}
            onReview={onReview}
            onReroll={onReroll}
            blwActive={blwActive}
            hiddenVeggieMode={hiddenVeggieMode}
            pickyEaterMode={pickyEaterMode}
          />
        )}
      </div>
    </div>
  );
}

function ConstraintBadges({
  blwActive,
  hiddenVeggieMode,
  pickyEaterMode,
}: {
  blwActive: boolean;
  hiddenVeggieMode: boolean;
  pickyEaterMode: boolean;
}) {
  const badges: { label: string; icon: IconName }[] = [];
  if (blwActive)        badges.push({ label: 'BLW active',     icon: 'family' });
  if (hiddenVeggieMode) badges.push({ label: 'Hidden veggies', icon: 'leaf'   });
  if (pickyEaterMode)   badges.push({ label: 'Picky eater',    icon: 'egg'    });
  if (badges.length === 0) return null;
  return (
    <div
      aria-label="Active meal plan constraints"
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 24,
      }}
    >
      {badges.map((b) => (
        <span
          key={b.label}
          className="chip"
          style={{
            background: 'var(--accent-soft)',
            color: 'var(--accent)',
            borderColor: 'transparent',
            fontSize: 11.5,
            padding: '5px 12px',
          }}
        >
          <Icon name={b.icon} size={12} />
          {b.label}
        </span>
      ))}
    </div>
  );
}

function ProgressPanel({
  step,
  fillPct,
  blwActive,
  hiddenVeggieMode,
  pickyEaterMode,
}: {
  step: number;
  fillPct: number;
  blwActive: boolean;
  hiddenVeggieMode: boolean;
  pickyEaterMode: boolean;
}) {
  return (
    <>
      <div style={{ textAlign: 'center', marginBottom: 48 }}>
        <div style={{ position: 'relative', display: 'inline-flex', marginBottom: 24 }}>
          <div
            style={{
              width: 88,
              height: 88,
              borderRadius: '50%',
              background: 'var(--accent-soft)',
              color: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'pp-pulse 2s ease-in-out infinite',
            }}
          >
            <Icon name="sparkle" size={36} />
          </div>
          <svg
            width="88"
            height="88"
            style={{ position: 'absolute', inset: 0, animation: 'pp-spin 4s linear infinite' }}
            viewBox="0 0 88 88"
            fill="none"
          >
            <circle cx="44" cy="44" r="42" stroke="var(--accent)" strokeWidth="1.5" strokeDasharray="2 8" opacity="0.7" />
          </svg>
        </div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--accent)',
            marginBottom: 12,
          }}
        >
          Building your week
        </div>
        <h1 className="h-display" style={{ fontSize: 48, margin: 0, color: 'var(--ink)', lineHeight: 1.05 }}>
          Hold tight,
          <br />
          <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>plating it up.</span>
        </h1>
      </div>

      <ConstraintBadges
        blwActive={blwActive}
        hiddenVeggieMode={hiddenVeggieMode}
        pickyEaterMode={pickyEaterMode}
      />

      <div className="card" style={{ padding: 24 }}>
        <div style={{ height: 4, background: 'var(--bg-deep)', borderRadius: 2, marginBottom: 20, overflow: 'hidden' }}>
          <div
            style={{
              height: '100%',
              width: `${fillPct}%`,
              background: 'var(--accent)',
              transition: 'width .5s ease',
              borderRadius: 2,
            }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {STEPS.map((s, i) => {
            const status: 'done' | 'active' | 'pending' =
              i < step ? 'done' : i === step ? 'active' : 'pending';
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '10px 4px',
                  opacity: status === 'pending' ? 0.4 : 1,
                  transition: 'opacity .3s',
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background:
                      status === 'done'
                        ? 'var(--accent)'
                        : status === 'active'
                        ? 'var(--accent-soft)'
                        : 'var(--surface-2)',
                    color: status === 'done' ? 'var(--accent-ink)' : 'var(--accent)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all .3s',
                  }}
                >
                  {status === 'done' ? (
                    <Icon name="check" size={14} stroke={2.4} />
                  ) : status === 'active' ? (
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 5,
                        background: 'var(--accent)',
                        animation: 'pp-pulse 1s ease-in-out infinite',
                      }}
                    />
                  ) : (
                    <Icon name={s.icon} size={14} />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{s.t}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{s.d}</div>
                </div>
                {status === 'active' && (
                  <div style={{ display: 'flex', gap: 3 }}>
                    {[0, 1, 2].map((n) => (
                      <div
                        key={n}
                        style={{
                          width: 4,
                          height: 4,
                          borderRadius: 2,
                          background: 'var(--accent)',
                          animation: 'pp-pulse 1.2s ease-in-out infinite',
                          animationDelay: `${n * 0.15}s`,
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function SuccessPanel({
  mealCount,
  onReview,
  onReroll,
  blwActive,
  hiddenVeggieMode,
  pickyEaterMode,
}: {
  mealCount: number;
  onReview: () => void;
  onReroll: () => void;
  blwActive: boolean;
  hiddenVeggieMode: boolean;
  pickyEaterMode: boolean;
}) {
  return (
    <div style={{ textAlign: 'center', animation: 'pp-pop .5s ease' }}>
      <div
        style={{
          width: 88,
          height: 88,
          borderRadius: '50%',
          background: 'var(--accent)',
          color: 'var(--accent-ink)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 24,
          boxShadow: '0 0 60px var(--accent-glow)',
        }}
      >
        <Icon name="check" size={42} stroke={2.4} />
      </div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--accent)',
          marginBottom: 12,
        }}
      >
        Plan ready
      </div>
      <h1 className="h-display" style={{ fontSize: 56, margin: 0, color: 'var(--ink)', lineHeight: 1.0 }}>
        {mealCount} meals,
        <br />
        <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>hand-picked.</span>
      </h1>
      <p style={{ fontSize: 16, color: 'var(--ink-3)', margin: '20px auto 16px', maxWidth: 460, lineHeight: 1.5 }}>
        Built around your tastes, your schedule, and your weekly budget.
      </p>
      <ConstraintBadges
        blwActive={blwActive}
        hiddenVeggieMode={hiddenVeggieMode}
        pickyEaterMode={pickyEaterMode}
      />
      <div style={{ display: 'inline-flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button className="btn btn-primary" onClick={onReview} style={{ padding: '14px 24px', fontSize: 14 }}>
          Review the plan <Icon name="arrow" size={14} />
        </button>
        <button className="btn btn-ghost" onClick={onReroll} style={{ padding: '14px 20px', fontSize: 14 }}>
          <Icon name="refresh" size={14} /> Re-roll
        </button>
      </div>
    </div>
  );
}

function ErrorPanel({
  message,
  onReroll,
  onClose,
}: {
  message: string;
  onReroll: () => void;
  onClose: () => void;
}) {
  return (
    <div style={{ textAlign: 'center', animation: 'pp-pop .5s ease' }}>
      <div
        style={{
          width: 88,
          height: 88,
          borderRadius: '50%',
          background: 'color-mix(in oklab, var(--danger) 18%, transparent)',
          color: 'var(--danger)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 24,
          border: '1px solid color-mix(in oklab, var(--danger) 35%, transparent)',
        }}
      >
        <Icon name="shield" size={36} />
      </div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--danger)',
          marginBottom: 12,
        }}
      >
        Generation failed
      </div>
      <h1 className="h-display" style={{ fontSize: 44, margin: 0, color: 'var(--ink)', lineHeight: 1.05 }}>
        That didn’t
        <br />
        <span style={{ color: 'var(--danger)', fontStyle: 'italic', fontWeight: 400 }}>quite work.</span>
      </h1>
      <p style={{ fontSize: 14, color: 'var(--ink-3)', margin: '20px auto 32px', maxWidth: 460, lineHeight: 1.5 }}>
        {message}
      </p>
      <div style={{ display: 'inline-flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button className="btn btn-primary" onClick={onReroll} style={{ padding: '14px 24px', fontSize: 14 }}>
          <Icon name="refresh" size={14} /> Try again
        </button>
        <button className="btn btn-ghost" onClick={onClose} style={{ padding: '14px 20px', fontSize: 14 }}>
          Close
        </button>
      </div>
    </div>
  );
}
